import { useEffect } from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Option,
    Select,
    Typography,
} from "@material-tailwind/react";
import {
    FormProvider,
    useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Trademark from "components/Trademark";
import RHFSetCheckbox from "components/form/rhf-set-checkbox";
import { GobbyTable, GobbyTableHead, GobbyTableBody, GobbyTableRow, GobbyTableHeader, GobbyTableCell } from "components/ui/gobby-table";

// `theme` has no input bound to it and the submit handler only reads
// answers_preset/answers_custom (the sets of selected answer ids), but it
// stays for payload parity.
function buildThemeAnswersValues(themeEdit, themeAnswers) {
    return {
        theme: themeEdit?.id || 0,
        answers_preset:
            themeAnswers?.theme_preset_answers.map(
                (e) => e.preset_answer_id,
            ) || [],
        answers_custom:
            themeAnswers?.theme_respondent_answers.map(
                (e) => e.respondent_answer_id,
            ) || [],
    };
}

// Lives inside the <Dialog> so every open remounts the form with fresh
// defaults from the current props (recipe P5). themeEdit or themeAnswers
// can change identity mid-open (SWR data can land while the dialog is
// open; SWR does not rebuild the reference on every parent render), so
// the reset effect below keys on those props to re-baseline the form.
function ThemeAnswersForm({
    themeEdit,
    themeAnswers,
    handleThemeAnswersSubmit,
    validationSchemaThemeAnswers,
    themeAnswersThemesState,
    setThemeAnswersThemesState,
    surveyQuestions,
    surveyRespondentAnswers,
    modQShown,
    setModQShown,
    handleClose,
}) {
    const methods = useForm({
        mode: "onChange",
        defaultValues: buildThemeAnswersValues(themeEdit, themeAnswers),
        // raw keeps the submitted values object uncast: yupResolver
        // otherwise submits Yup's cast values, and the schema's
        // theme: Yup.string() would stringify the numeric themeEdit.id
        // on the way out.
        resolver: yupResolver(validationSchemaThemeAnswers, {}, { raw: true }),
    });
    const { reset } = methods;
    // Read both flags up front: formState is a subscription proxy, so a
    // conditional read (`isValid && isDirty`) short-circuits before
    // isDirty while isValid is false. RHF then never subscribes to dirty
    // updates and isDirty stays frozen at false.
    const { isValid, isDirty } = methods.formState;

    useEffect(() => {
        reset(buildThemeAnswersValues(themeEdit, themeAnswers), {
            keepErrors: true,
        });
    }, [themeEdit, themeAnswers, reset]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleThemeAnswersSubmit)}>
                <DialogBody className="dialog__body">
                    <Typography className="px-4 pt-2 font-body">
                        Apply this theme to any/all answers to a
                        particular question. First choose an answer from
                        either Gobby
                        <Trademark /> Response or a preset answers
                        question from the list
                    </Typography>
                    <div className="flex flex-row-reverse mb-4">
                        <Button
                            onClick={() =>
                                setThemeAnswersThemesState("preset")
                            }
                            color="white"
                            className="btn btn--secondary ml-2"
                            variant="filled"
                        >
                            Preset Answers
                        </Button>
                        {!!surveyRespondentAnswers && (
                            <Button
                                onClick={() =>
                                    setThemeAnswersThemesState(
                                        "response",
                                    )
                                }
                                color="white"
                                className="btn btn--secondary"
                                variant="filled"
                            >
                                Gobby
                                <Trademark /> Response
                            </Button>
                        )}
                    </div>
                    {!!surveyQuestions && (
                        <Select
                            className="bg-white"
                            label="Select a question from the list"
                            value={modQShown?.toString()}
                            onChange={(e) => {
                                const indexInt = parseInt(e);
                                setModQShown(indexInt);
                            }}
                            menuProps={{
                                className:
                                    "max-h-[calc(100%+7rem)] overflow-y-auto",
                            }}
                        >
                            {surveyQuestions.map((q, index) => (
                                <Option key={q.id} value={`${index}`}>
                                    Question {q.position}: {q.text}
                                </Option>
                            ))}
                        </Select>
                    )}
                    {!!(themeAnswersThemesState === "preset") && (
                        <div className="p-2 rounded-lg bg-white mt-2 w-full">
                            <GobbyTable>
                                <GobbyTableHead>
                                    <GobbyTableRow>
                                        <GobbyTableHeader className="w-5/6">
                                            Preset Response
                                        </GobbyTableHeader>
                                        <GobbyTableHeader className="w-1/6">
                                            Theme
                                        </GobbyTableHeader>
                                    </GobbyTableRow>
                                </GobbyTableHead>
                                <GobbyTableBody>
                                    {surveyQuestions[
                                        modQShown
                                    ]?.preset_answers.map(
                                        (answer) => (
                                            <GobbyTableRow
                                                key={answer.id}
                                            >
                                                <GobbyTableCell className="">
                                                    {
                                                        answer.text
                                                    }
                                                </GobbyTableCell>
                                                <GobbyTableCell className="text-right md:pr-4">
                                                    <RHFSetCheckbox
                                                        name="answers_preset"
                                                        value={answer.id}
                                                    />
                                                </GobbyTableCell>
                                            </GobbyTableRow>
                                        ),
                                    )}
                                </GobbyTableBody>
                            </GobbyTable>
                        </div>
                    )}
                    {!!(themeAnswersThemesState === "response") && (
                        <div className="p-2 rounded-lg bg-white mt-2 w-full">
                            <GobbyTable>
                                <GobbyTableHead className="">
                                    <GobbyTableRow>
                                        <GobbyTableHeader className="w-5/6">
                                            Gobby
                                            <Trademark />{" "}
                                            Response
                                        </GobbyTableHeader>
                                        <GobbyTableHeader className="w-1/6">
                                            Theme
                                        </GobbyTableHeader>
                                    </GobbyTableRow>
                                </GobbyTableHead>
                                <GobbyTableBody>
                                    {surveyRespondentAnswers?.[
                                        modQShown
                                    ]?.respondent_answers.map(
                                        (answer) => (
                                            <GobbyTableRow
                                                key={answer.id}
                                            >
                                                <GobbyTableCell className="">
                                                    {
                                                        answer.text
                                                    }
                                                </GobbyTableCell>
                                                <GobbyTableCell className="text-right md:pr-4">
                                                    <RHFSetCheckbox
                                                        name="answers_custom"
                                                        value={answer.id}
                                                    />
                                                </GobbyTableCell>
                                            </GobbyTableRow>
                                        ),
                                    )}
                                </GobbyTableBody>
                            </GobbyTable>
                        </div>
                    )}
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <div className="">
                        <Button
                            type="button"
                            className="btn btn--secondary mr-2"
                            variant="filled"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!(isValid && isDirty)}
                            onClick={handleClose}
                            className="btn btn--primary"
                            variant="filled"
                        >
                            Save
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </FormProvider>
    );
}

export default function ThemeAnswersDialog({
    showThemeAnswersDialog,
    setShowThemeAnswersDialog,
    themeEdit,
    handleThemeAnswersSubmit,
    validationSchemaThemeAnswers,
    themeAnswersThemesState,
    setThemeAnswersThemesState,
    surveyQuestions,
    surveyRespondentAnswers,
    modQShown,
    setModQShown,
    themeAnswers,
}) {
    const handleClose = () => {
        setShowThemeAnswersDialog((showThemeAnswersDialog) => !showThemeAnswersDialog);
    };
    return (
        <Dialog
            open={showThemeAnswersDialog}
            handler={handleClose}
            className="dialog"
        >
            <DialogHeader className="dialog__header">Apply Theme</DialogHeader>
            <ThemeAnswersForm
                themeEdit={themeEdit}
                themeAnswers={themeAnswers}
                handleThemeAnswersSubmit={handleThemeAnswersSubmit}
                validationSchemaThemeAnswers={validationSchemaThemeAnswers}
                themeAnswersThemesState={themeAnswersThemesState}
                setThemeAnswersThemesState={setThemeAnswersThemesState}
                surveyQuestions={surveyQuestions}
                surveyRespondentAnswers={surveyRespondentAnswers}
                modQShown={modQShown}
                setModQShown={setModQShown}
                handleClose={handleClose}
            />
        </Dialog>
    );
}
